import { throttle } from 'throttle-debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon, XIcon, ChevronDownIcon, ExternalLinkIcon } from 'lucide-react';
import { useEventListener } from 'usehooks-ts';
import { Link } from 'wouter';
import { useAuth } from '@/hooks/auth';
import { HistoryTableSearchType } from '@shared/historyApiTypes';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select, SelectContent, SelectItem, SelectSeparator,
    SelectTrigger, SelectValue,
} from '@/components/ui/select';
import InlineCode from '@/components/InlineCode';

export const availableSearchTypes = [
    { value: 'actionId',    label: 'Action ID',   placeholder: 'XXXX-XXXX',                         description: 'Search by action ID.' },
    { value: 'reason',      label: 'Reason',       placeholder: 'Enter part of the reason',          description: 'Search by reason contents.' },
    { value: 'identifiers', label: 'Player IDs',   placeholder: 'License, Discord, Steam, etc.',    description: 'Search by player IDs (comma-separated).' },
] as const;

export const SEARCH_ANY_STRING = '!any';

// eslint-disable-next-line @typescript-eslint/no-explicit-any, react-refresh/only-export-components
export const throttleFunc = throttle(1250, (func: any) => { func(); }, { noLeading: true });

export type HistorySearchBoxReturnStateType = {
    search: HistoryTableSearchType;
    filterbyType?: string;
    filterbyAdmin?: string;
};

type Props = {
    doSearch: (search: HistoryTableSearchType, filterbyType: string | undefined, filterbyAdmin: string | undefined) => void;
    initialState: HistorySearchBoxReturnStateType;
    adminStats: { name: string; actions: number }[];
};

export function HistorySearchBox({ doSearch, initialState, adminStats }: Props) {
    const { authData } = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);
    const [currSearchType, setCurrSearchType] = useState<string>(initialState.search.type);
    const [hasSearchText, setHasSearchText] = useState(!!initialState.search.value);
    const [typeFilter, setTypeFilter] = useState(initialState.filterbyType);
    const [adminNameFilter, setAdminNameFilter] = useState(initialState.filterbyAdmin);

    const updateSearch = () => {
        if (!inputRef.current) return;
        doSearch(
            { value: inputRef.current.value.trim(), type: currSearchType },
            typeFilter !== SEARCH_ANY_STRING ? typeFilter : undefined,
            adminNameFilter !== SEARCH_ANY_STRING ? adminNameFilter : undefined,
        );
    };

    useEffect(() => { updateSearch(); }, [currSearchType, typeFilter, adminNameFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            throttleFunc.cancel({ upcomingOnly: true });
            updateSearch();
        } else if (e.key === 'Escape') {
            inputRef.current!.value = '';
            throttleFunc(updateSearch);
            setHasSearchText(false);
        } else {
            throttleFunc(updateSearch);
            setHasSearchText(true);
        }
    };

    const clearSearch = () => {
        inputRef.current!.value = '';
        throttleFunc.cancel({ upcomingOnly: true });
        updateSearch();
        setHasSearchText(false);
    };

    useEventListener('keydown', (e: KeyboardEvent) => {
        if (e.ctrlKey && e.code === 'KeyF') {
            inputRef.current?.focus();
            e.preventDefault();
        }
    });

    const selectedSearchType = availableSearchTypes.find(t => t.value === currSearchType)!;
    if (!authData) throw new Error('authData is not available');

    const filteredAdmins = useMemo(() =>
        adminStats?.filter(a => a.name !== authData.name) ?? [],
    [adminStats, authData.name]);
    const selfActionCount = useMemo(() =>
        adminStats?.find(a => a.name === authData.name)?.actions ?? 0,
    [adminStats, authData.name]);

    const TYPE_FILTERS = [
        { value: SEARCH_ANY_STRING, label: 'All Types' },
        { value: 'ban',             label: 'Bans' },
        { value: 'warn',            label: 'Warns' },
    ];

    return (
        <div className="flex flex-col gap-3 px-4 py-3 rounded-xl border border-white/[0.06] bg-white/[0.01]">
            {/* Search row */}
            <div className="flex flex-wrap gap-2">
                {/* Input */}
                <div className="relative flex-1 min-w-48">
                    <SearchIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" />
                    <input
                        ref={inputRef}
                        type="text"
                        autoFocus
                        autoCapitalize="off"
                        autoCorrect="off"
                        defaultValue={initialState.search.value}
                        placeholder={selectedSearchType.placeholder}
                        onKeyDown={handleInputKeyDown}
                        className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg pl-8 pr-16 py-2 text-xs font-mono text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#00ffd1]/30 focus:ring-1 focus:ring-[#00ffd1]/15 transition-all"
                    />
                    <div className="absolute right-2 inset-y-0 flex items-center gap-1">
                        {hasSearchText ? (
                            <button onClick={clearSearch} className="text-slate-600 hover:text-slate-400 p-0.5">
                                <XIcon size={13} />
                            </button>
                        ) : (
                            <InlineCode className="text-[10px] tracking-wide text-slate-600">ctrl+f</InlineCode>
                        )}
                    </div>
                </div>

                {/* Search type dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.07] bg-white/[0.02] text-xs font-mono font-semibold text-slate-400 hover:border-white/10 hover:text-slate-200 transition-all whitespace-nowrap">
                            by {selectedSearchType.label}
                            <ChevronDownIcon size={12} className="text-slate-600" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-48">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Search By</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuRadioGroup value={currSearchType} onValueChange={setCurrSearchType}>
                            {availableSearchTypes.map(st => (
                                <DropdownMenuRadioItem key={st.value} value={st.value} className="text-xs font-mono">
                                    {st.label}
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                            <Link href="/players" className="text-xs flex items-center gap-1.5">
                                <ExternalLinkIcon size={11} /> Search in Players
                            </Link>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap gap-2 items-center">
                {/* Type filter tabs */}
                <div className="flex items-center gap-1 p-0.5 rounded-lg border border-white/[0.06] bg-black/20">
                    {TYPE_FILTERS.map(f => (
                        <button
                            key={f.value}
                            onClick={() => setTypeFilter(f.value)}
                            className={`px-3 py-1 rounded-md text-[11px] font-bold font-mono uppercase tracking-wider transition-all duration-100 ${
                                typeFilter === f.value
                                    ? 'bg-white/[0.08] text-white'
                                    : 'text-slate-600 hover:text-slate-300'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Admin filter */}
                <Select value={adminNameFilter} onValueChange={setAdminNameFilter}>
                    <SelectTrigger className="h-[30px] w-auto min-w-[140px] text-xs font-mono border-white/[0.07] bg-white/[0.02] text-slate-400">
                        <SelectValue placeholder="All Admins" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SEARCH_ANY_STRING} className="text-xs font-mono">All Admins</SelectItem>
                        {selfActionCount > 0 && (
                            <>
                                <SelectSeparator />
                                <SelectItem value={authData.name} className="text-xs font-mono">
                                    {authData.name} (you · {selfActionCount})
                                </SelectItem>
                            </>
                        )}
                        {filteredAdmins.length > 0 && (
                            <>
                                <SelectSeparator />
                                {filteredAdmins.map(a => (
                                    <SelectItem key={a.name} value={a.name} className="text-xs font-mono">
                                        {a.name} ({a.actions})
                                    </SelectItem>
                                ))}
                            </>
                        )}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
